import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import userContext from "../services/userContext";
import queries from './queries';
import fragments from './fragments';
import update from 'immutability-helper';
import { refetchChangedQueues } from '../services/queueUpdateService';


const createACLMutation = gql`
mutation createACL($userEmail: String!, $objectId: ID!, $objectType: ACLObjectType!, $role: ACLRole!) {
    createACL(userEmail: $userEmail, objectId: $objectId, objectType: $objectType, role: $role) {
        ...aclFields
    }
}
${fragments.aclFields}`;

const createACLMutationHoc = graphql(createACLMutation, {
    props: ({mutate}) => ({
        createACL: (userEmail, objectId, objectType, role) => mutate({
            variables: {userEmail, objectId, objectType, role},
            updateQueries: {
                aclList: (prev, {mutationResult}) => {
                    const newACL = mutationResult.data.createACL;
                    return update(prev, {
                        aclList: {
                            $push: [newACL]
                        }
                    });
                }
            }
        })
    })
});

const deleteACLMutation = gql`
mutation deleteACL($aclId: ID!) {
    deleteACL(aclId: $aclId)
}`;

const deleteACLMutationHoc = graphql(deleteACLMutation, {
    props: ({mutate}) => ({
        deleteACL: (aclId) => mutate({
            variables: {aclId},
            updateQueries: {
                aclList: (prev) => {
                    let index = prev.aclList.findIndex(acl => acl.id === aclId);
                    return update(prev, {
                        aclList: {
                            $splice: [[index, 1]]
                        }
                    });
                }
            }
        })
    })
});

const createProjectMutation = gql`
mutation createProject($projectName: String!) {
  createProject(projectName: $projectName) {
    ...ProjectInProjectList
  }
}
${fragments.projectFieldsForList}`;

const createProjectMutationHoc = graphql(createProjectMutation, {
    props: ({mutate}) => ({
        createProject: (projectName) => mutate({
            variables: {projectName},
            updateQueries: {
                projects: (prev, {mutationResult}) => {
                    const newProject = mutationResult.data.createProject;
                    return update(prev, {
                        projects: {
                            $push: [newProject]
                        }
                    });
                }
            }
        })
    })
});

const deleteProjectMutation = gql`
mutation deleteProject($projectId: ID!) {
  deleteProject(projectId: $projectId) {
    id
  }
}`;

const deleteProjectMutationHoc = graphql(deleteProjectMutation, {
    props: ({mutate}) => ({
        deleteProject: (projectId) => mutate({
            variables: {projectId},
            updateQueries: {
                projects: (prev) => {
                    let index = prev.projects.findIndex(project => project.id === projectId);
                    return update(prev, {
                        projects: {
                            $splice: [[index, 1]]
                        }
                    });
                }
            }
        })
    })
});

const generateNewAPIKeyMutation = gql`
mutation generateNewAPIKey($projectId: ID!) {
  generateNewAPIKey(projectId: $projectId) {
    ...ProjectInProjectList
  }
}
${fragments.projectFieldsForList}`;

const generateNewAPIKeyMutationHoc = graphql(generateNewAPIKeyMutation, {
    props: ({mutate}) => ({
        generateNewAPIKey: (projectId) => mutate({
            variables: {projectId}
        })
    })
});

const updateItemListViewTemplateMutation = gql`
mutation updateItemListViewTemplate($projectId: ID!, $itemListViewTemplateInput: ItemListViewTemplateInput!) {
  updateItemListViewTemplate(projectId: $projectId, itemListViewTemplateInput: $itemListViewTemplateInput) {
    ...projectFields  
  }
}
${fragments.projectFields}`;

const updateItemListViewTemplateMutationHoc = graphql(updateItemListViewTemplateMutation, {
    props: ({mutate}) => ({
        updateItemListViewTemplate: (projectId, itemListViewTemplateInput) => mutate({
            variables: {projectId, itemListViewTemplateInput}
        })
    })
});

const createPluginMutation = gql`
mutation createPlugin($pluginInput: PluginInput!) {
  createPlugin(pluginInput: $pluginInput) {
    ...pluginFields
  }
}
${fragments.pluginFields}`;

const createPluginMutationHoc = graphql(createPluginMutation, {
    props: ({mutate}) => ({
        createPlugin: (pluginInput) => mutate({
            variables: {pluginInput},
            updateQueries: {
                project: (prev, {mutationResult}) => {
                    const newPlugin = mutationResult.data.createPlugin;
                    return update(prev, {
                        project: {
                            plugins: {
                                $push: [newPlugin]
                            }
                        }
                    });
                }
            }
        })
    })
});

const updatePluginMutation = gql`
mutation updatePlugin($pluginId: ID!, $pluginInput: PluginInput!) {
  updatePlugin(pluginId: $pluginId, pluginInput: $pluginInput) {
    ...pluginFields
  }
}
${fragments.pluginFields}`;

const updatePluginMutationHoc = graphql(updatePluginMutation, {
    props: ({mutate}) => ({
        updatePlugin: (pluginId, pluginInput) => mutate({
            variables: {pluginId, pluginInput}
        })
    })
});

const updatePluginsOrderMutation = gql`
mutation updatePluginsOrderMutation($projectId: ID!, $pluginIds: [ID!]!) {
  updatePluginsOrder(projectId: $projectId, pluginIds: $pluginIds)
}`;

const updatePluginsOrderMutationHoc = graphql(updatePluginsOrderMutation, {
    props: ({mutate}) => ({
        updatePluginsOrder: (projectId, pluginIds) => mutate({
            variables: {projectId, pluginIds},
            updateQueries: {
                project: (prev) => {
                    let pluginIdToIndexMap = pluginIds.reduce((map, pluginId, index) => {map[pluginId] = index; return map;}, {});
                    let plugins = prev.project.plugins.slice().sort((pluginA, pluginB) => pluginIdToIndexMap[pluginA.id] - pluginIdToIndexMap[pluginB.id]);
                    return update(prev, {
                        project: {
                            plugins: {$set: plugins}
                        }
                    });
                }
            }
        })
    })
});

const deletePluginMutation = gql`
mutation deletePlugin($pluginId: ID!) {
  deletePlugin(pluginId: $pluginId) {
    ...pluginFields
  }
}
${fragments.pluginFields}`;

const deletePluginMutationHoc = graphql(deletePluginMutation, {
    props: ({mutate}) => ({
        deletePlugin: (pluginId) => mutate({
            variables: {pluginId},
            updateQueries: {
                project: (prev) => {
                    let index = prev.project.plugins.findIndex(plugin => plugin.id === pluginId);
                    return update(prev, {
                        project: {
                            plugins: {
                                $splice: [[index, 1]]
                            }
                        }
                    });
                }
            }
        })
    })
});

const createWebhookMutation = gql`
mutation createWebhook($projectId: ID!, $webhookInput: WebhookInput!) {
  createWebhook(projectId: $projectId, webhookInput: $webhookInput) {
    ...webhookFields
  }
}
${fragments.webhookFields}`;

const createWebhookMutationHoc = graphql(createWebhookMutation, {
    props: ({mutate}) => ({
        createWebhook: (projectId, webhookInput) => mutate({
            variables: {projectId, webhookInput},
            updateQueries: {
                project: (prev, {mutationResult}) => {
                    const newWebhook = mutationResult.data.createWebhook;
                    return update(prev, {
                        project: {
                            webhooks: {
                                $push: [newWebhook]
                            }
                        }
                    });
                }
            }
        })
    })
});

const updateWebhookMutation = gql`
mutation updateWebhook($webhookId: ID!, $webhookInput: WebhookInput!) {
  updateWebhook(webhookId: $webhookId, webhookInput: $webhookInput) {
    ...webhookFields
  }
}
${fragments.webhookFields}`;

const updateWebhookMutationHoc = graphql(updateWebhookMutation, {
    props: ({mutate}) => ({
        updateWebhook: (webhookId, webhookInput) => mutate({
            variables: {webhookId, webhookInput}
        })
    })
});

const deleteWebhookMutation = gql`
mutation deleteWebhook($webhookId: ID!) {
  deleteWebhook(webhookId: $webhookId) {
    ...webhookFields
  }
}
${fragments.webhookFields}`;

const deleteWebhookMutationHoc = graphql(deleteWebhookMutation, {
    props: ({mutate}) => ({
        deleteWebhook: (webhookId) => mutate({
            variables: {webhookId},
            updateQueries: {
                project: (prev) => {
                    let index = prev.project.webhooks.findIndex(webhook => webhook.id === webhookId);
                    return update(prev, {
                        project: {
                            webhooks: {
                                $splice: [[index, 1]]
                            }
                        }
                    });
                }
            }
        })
    })
});

const createBoardMutation = gql`
mutation createBoard($projectId: ID!, $boardInput: BoardInput!) {
  createBoard(projectId: $projectId, boardInput: $boardInput) {
    ...boardFields
  }
}
${fragments.boardFields}`;

const createBoardMutationHoc = graphql(createBoardMutation, {
    props: ({mutate}) => ({
        createBoard: (projectId, boardInput) => {
            return mutate({
                variables: {projectId, boardInput},
                updateQueries: {
                    project: (prev, {mutationResult}) => {
                        const newBoard = mutationResult.data.createBoard;
                        return update(prev, {
                            project: {
                                boards: {
                                    $push: [newBoard]
                                }
                            }
                        });
                    }
                }
            });
        }
    })
});

const updateBoardMutation = gql`
mutation updateBoard($boardId: ID!, $boardInput: BoardInput!) {
  updateBoard(boardId: $boardId, boardInput: $boardInput) {
    ...boardFields
  }
}
${fragments.boardFields}`;

const updateBoardMutationHoc = graphql(updateBoardMutation, {
    props: ({mutate}) => ({
        updateBoard: (boardId, boardInput) => mutate({
            variables: {boardId, boardInput}
        })
    })
});

const updateBoardsOrderMutation = gql`
mutation updateBoardsOrder($projectId: ID!, $boardIds: [ID!]!) {
  updateBoardsOrder(projectId: $projectId, boardIds: $boardIds)
}`;

const updateBoardsOrderMutationHoc = graphql(updateBoardsOrderMutation, {
    props: ({mutate}) => ({
        updateBoardsOrder: (projectId, boardIds) => mutate({
            variables: {projectId, boardIds},
            updateQueries: {
                project: (prev) => {
                    let boardIdToIndexMap = boardIds.reduce((map, boardId, index) => {map[boardId] = index; return map;}, {});
                    let boards = prev.project.boards.slice().sort((boardA, boardB) => boardIdToIndexMap[boardA.id] - boardIdToIndexMap[boardB.id]);
                    return update(prev, {
                        project: {
                            boards: {$set: boards}
                        }
                    });
                }
            }
        })
    })
});

const deleteBoardMutation = gql`
mutation deleteBoard($boardId: ID!) {
  deleteBoard(boardId: $boardId) {
    id
  }
}`;

const deleteBoardMutationHoc = graphql(deleteBoardMutation, {
    props: ({mutate}) => ({
        deleteBoard: (boardId) => mutate({
            variables: {boardId},
            updateQueries: {
                project: (prev) => {
                    let index = prev.project.boards.findIndex(board => board.id === boardId);
                    return update(prev, {
                        project: {
                            boards: {
                                $splice: [[index, 1]]
                            }
                        }
                    });
                }
            }
        })
    })
});

const createQueueMutation = gql`
mutation createQueue($queueInput: QueueInput!) {
  createQueue(queueInput: $queueInput) {
    ...QueueFragment
  }
}
${fragments.queueFragment}`;

const createQueueMutationHoc = graphql(createQueueMutation, {
    props: ({mutate}) => ({
        createQueue: (queueInput) => mutate({
            variables: {queueInput},
            updateQueries: {
                boardQuery: (prev, {mutationResult}) => {
                    const newQueue = mutationResult.data.createQueue;
                    return update(prev, {
                        board: {
                            queues: {
                                $push: [newQueue]
                            }
                        }
                    });
                },
                queuesQuery: (prev, {mutationResult}) => {
                    const newQueue = mutationResult.data.createQueue;
                    return update(prev, {
                        queues: {
                            $push: [newQueue]
                        }
                    });
                }
            }
        })
    })
});

const updateQueueMutation = gql`
mutation updateQueue($queueId: ID!, $queueInput: QueueInput!) {
  updateQueue(queueId: $queueId, queueInput: $queueInput) {
    ...QueueFragment
  }
}
${fragments.queueFragment}`;

const updateQueueMutationHoc = graphql(updateQueueMutation, {
    props: ({mutate}) => ({
        updateQueue: (queueId, queueInput) => mutate({
            variables: {queueId, queueInput},
            refetchQueries: [{
                query: queries.itemListQuery,
                variables: {queueId}
            }]
        })
    })
});

const updateQueuesOrderMutation = gql`
mutation updateQueuesOrder($boardId: ID!, $queueIds: [ID!]!) {
  updateQueuesOrder(boardId: $boardId, queueIds: $queueIds)
}`;

const updateQueuesOrderMutationHoc = graphql(updateQueuesOrderMutation, {
    props: ({mutate}) => ({
        updateQueuesOrder: (boardId, queueIds) => mutate({
            variables: {boardId, queueIds},
            updateQueries: {
                boardQuery: (prev) => {
                    let queueIdToIndexMap = queueIds.reduce((map, queueId, index) => {map[queueId] = index; return map;}, {});
                    let queues = prev.board.queues.slice().sort((queueA, queueB) => queueIdToIndexMap[queueA.id] - queueIdToIndexMap[queueB.id]);
                    return update(prev, {
                        board: {
                            queues: {$set: queues}
                        }
                    });
                }
            }
        })
    })
});

const deleteQueueMutation = gql`
mutation deleteQueue($queueId: ID!) {
  deleteQueue(queueId: $queueId) {
    id
  }
}`;

const deleteQueueMutationHoc = graphql(deleteQueueMutation, {
    props: ({mutate}) => ({
        deleteQueue: (queueId) => mutate({
            variables: {queueId},
            updateQueries: {
                boardQuery: (prev) => {
                    let index = prev.board.queues.findIndex(queue => queue.id === queueId);
                    return update(prev, {
                        board: {
                            queues: {
                                $splice: [[index, 1]]
                            }
                        }
                    });
                },
                queuesQuery: (prev) => {
                    let index = prev.queues.findIndex(queue => queue.id === queueId);
                    return update(prev, {
                        queues: {
                            $splice: [[index, 1]]
                        }
                    });
                }
            }
        })
    })
});

const createQueueItemButtonMutation = gql`
mutation createQueueItemButton($queueId: ID!, $queueItemButtonInput: QueueItemButtonInput!) {
  createQueueItemButton(queueId: $queueId, queueItemButtonInput: $queueItemButtonInput) {
    ...QueueItemButtonFragment
  }
}
${fragments.queueItemButtonFragment}`;

const createQueueItemButtonMutationHoc = graphql(createQueueItemButtonMutation, {
    props: ({mutate}) => ({
        createQueueItemButton: (queueId, queueItemButtonInput) => mutate({
            variables: {queueId, queueItemButtonInput},
            updateQueries: {
                boardQuery: (prev, {mutationResult}) => {
                    let index = prev.board.queues.findIndex(queue => queue.id === queueId);
                    const newQueueItemButton = mutationResult.data.createQueueItemButton;
                    return update(prev, {
                        board: {
                            queues: {
                                [index]: {
                                    itemViewButtons: {
                                        $push: [newQueueItemButton]
                                    }
                                }
                            }
                        }
                    });
                }
            }
        })
    })
});

const updateQueueItemButtonMutation = gql`
mutation updateQueueItemButton($queueItemButtonId: ID!, $queueItemButtonInput: QueueItemButtonInput!) {
  updateQueueItemButton(queueItemButtonId: $queueItemButtonId, queueItemButtonInput: $queueItemButtonInput) {
    ...QueueItemButtonFragment
  }
}
${fragments.queueItemButtonFragment}`;

const updateQueueItemButtonMutationHoc = graphql(updateQueueItemButtonMutation, {
    props: ({mutate}) => ({
        updateQueueItemButton: (queueItemButtonId, queueItemButtonInput) => mutate({
            variables: {queueItemButtonId, queueItemButtonInput}
        })
    })
});

const deleteQueueItemButtonMutation = gql`
mutation deleteQueueItemButton($queueItemButtonId: ID!) {
  deleteQueueItemButton(queueItemButtonId: $queueItemButtonId) {
    id
  }
}`;

const deleteQueueItemButtonMutationHoc = graphql(deleteQueueItemButtonMutation, {
    props: ({mutate}) => ({
        deleteQueueItemButton: (queueId, queueItemButtonId) => mutate({
            variables: {queueItemButtonId},
            updateQueries: {
                boardQuery: (prev) => {
                    let queueIndex = prev.board.queues.findIndex(queue => queue.id === queueId);
                    let buttonIndex = prev.board.queues[queueIndex].itemViewButtons.findIndex(button => button.id === queueItemButtonId);
                    return update(prev, {
                        board: {
                            queues: {
                                [queueIndex]: {
                                    itemViewButtons: {
                                        $splice: [[buttonIndex, 1]]
                                    }
                                }
                            }
                        }
                    });
                }
            }
        })
    })
});

const createItemMutation = gql`
mutation createItem($projectId: ID!, $clientId: String, $data: JSON, $tags: [String]) {
  createItem(projectId: $projectId, clientId: $clientId, data: $data, tags: $tags) {
    ...ItemFragment
  }
}
${fragments.itemFragment}`;

const createItemMutationHoc = graphql(createItemMutation, {
    props: ({ownProps, mutate}) => ({
        createItem: (projectId, clientId, data, tags) => mutate({
            variables: {projectId, clientId, data, tags}
        }).then(result => {
            let createdItem = result.data.createItem;
            refetchChangedQueues(projectId, createdItem);
            return createdItem;
        })
    })
});

const updateItemMutation = gql`
mutation updateItem($itemId: ID!, $data: JSON, $tags: JSON) {
  updateItem(itemId: $itemId, data: $data, tags: $tags) {
    ...ItemFragment
  }
}
${fragments.itemFragment}`;

const updateItemMutationHoc = graphql(updateItemMutation, {
    props: ({ownProps, mutate}) => ({
        updateItem: (projectId, item, data, tags) => mutate({
            variables: {itemId: item.id, data, tags},
            refetchQueries: [{
                query: queries.itemLogsQuery,
                variables: {itemId: item.id}
            }]
        }).then(result => {
            let updatedItem = result.data.updateItem;
            refetchChangedQueues(projectId, updatedItem, item);
            return updatedItem;
        })
    })
});

const reportEventMutation = gql`
mutation reportEvent($itemId: ID!, $eventName: String!) {
    reportEvent(itemId: $itemId, eventName: $eventName) {
        ...ItemFragment
    }
}
${fragments.itemFragment}`;

const reportEventMutationHoc = graphql(reportEventMutation, {
    props: ({mutate}) => ({
        reportEvent: (projectId, item, eventName) => mutate({
            variables: {itemId: item.id, eventName},
            refetchQueries: [{
                query: queries.itemLogsQuery,
                variables: {itemId: item.id}
            }]
        }).then(result => {
            let updatedItem = result.data.reportEvent;
            refetchChangedQueues(projectId, updatedItem, item);
            return updatedItem;
        })
    })
});

const assignCurrentUserMutation = gql`
mutation assignCurrentUser($itemId: ID!, $flowId: ID!) {
    assignCurrentUser(itemId: $itemId, flowId: $flowId) {
        ...ItemFragment
    }
}
${fragments.itemFragment}`;

const assignCurrentUserMutationHoc = graphql(assignCurrentUserMutation, {
    props: ({mutate}) => ({
        assignCurrentUser: (projectId, item, flowId) => mutate({
            variables: {itemId: item.id, flowId},
            refetchQueries: [{
                query: queries.itemLogsQuery,
                variables: {itemId: item.id}
            }]
        }).then(result => {
            let updatedItem = result.data.assignCurrentUser;
            refetchChangedQueues(projectId, updatedItem, item);
            return updatedItem;
        })
    })
});

const executeActionMutation = gql`
mutation executeActionMutation($itemId: ID!, $actionId: ID!) {
    executeAction(itemId: $itemId, actionId: $actionId) {
        ...ItemFragment
    }
}
${fragments.itemFragment}`;

const executeActionMutationHoc = graphql(executeActionMutation, {
    props: ({mutate}) => ({
        executeAction: (projectId, item, actionId) => mutate({
            variables: {itemId: item.id, actionId},
            refetchQueries: [{
                query: queries.itemLogsQuery,
                variables: {itemId: item.id}
            }]
        }).then(result => {
            let updatedItem = result.data.executeAction;
            refetchChangedQueues(projectId, updatedItem, item);
            return updatedItem;
        })
    })
});

const updateUserName = gql`
mutation updateUserNameMutation($name: String!) {
    updateUserName(name: $name)
}`;

const updateUserNameHoc = graphql(updateUserName, {
    props: ({mutate}) => ({
        updateUserName: (name) => mutate({
            variables: {name}
        }).then(() => {
            return userContext.fetchCurrentUser();
        })
    })
});


export default {
    // ACL
    createACLMutation,
    createACLMutationHoc,
    deleteACLMutation,
    deleteACLMutationHoc,

    // Project
    createProjectMutation,
    createProjectMutationHoc,
    deleteProjectMutation,
    deleteProjectMutationHoc,
    generateNewAPIKeyMutation,
    generateNewAPIKeyMutationHoc,
    updateItemListViewTemplateMutation,
    updateItemListViewTemplateMutationHoc,

    // Plugin
    createPluginMutation,
    createPluginMutationHoc,
    updatePluginMutation,
    updatePluginMutationHoc,
    updatePluginsOrderMutation,
    updatePluginsOrderMutationHoc,
    deletePluginMutation,
    deletePluginMutationHoc,

    // Webhook
    createWebhookMutation,
    createWebhookMutationHoc,
    updateWebhookMutation,
    updateWebhookMutationHoc,
    deleteWebhookMutation,
    deleteWebhookMutationHoc,

    // Board
    createBoardMutation,
    createBoardMutationHoc,
    updateBoardMutation,
    updateBoardMutationHoc,
    updateBoardsOrderMutation,
    updateBoardsOrderMutationHoc,
    deleteBoardMutation,
    deleteBoardMutationHoc,

    // Queue
    createQueueMutation,
    createQueueMutationHoc,
    updateQueueMutation,
    updateQueueMutationHoc,
    updateQueuesOrderMutation,
    updateQueuesOrderMutationHoc,
    deleteQueueMutation,
    deleteQueueMutationHoc,

    // QueueItemButton
    createQueueItemButtonMutation,
    createQueueItemButtonMutationHoc,
    updateQueueItemButtonMutation,
    updateQueueItemButtonMutationHoc,
    deleteQueueItemButtonMutation,
    deleteQueueItemButtonMutationHoc,

    // Item
    createItemMutation,
    createItemMutationHoc,
    updateItemMutation,
    updateItemMutationHoc,
    reportEventMutation,
    reportEventMutationHoc,
    assignCurrentUserMutation,
    assignCurrentUserMutationHoc,
    executeActionMutation,
    executeActionMutationHoc,

    // User
    updateUserName,
    updateUserNameHoc
};



// WEBPACK FOOTER //
// ./src/graphql/mutations.js